import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";

function publicClient() {
  const key = process.env.SUPABASE_PUBLISHABLE_KEY!;
  return createClient(process.env.SUPABASE_URL!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const h = new Headers(init?.headers);
        if (key.startsWith("sb_") && h.get("Authorization") === `Bearer ${key}`) h.delete("Authorization");
        h.set("apikey", key);
        return fetch(input, { ...init, headers: h });
      },
    },
  });
}

export const getMyPortfolioSettings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data } = await supabase.from("lxp_portfolio_settings").select("*").eq("user_id", userId).maybeSingle();
    return { settings: data ?? null };
  });

export const saveMyPortfolioSettings = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) =>
    z.object({
      display_name: z.string().min(2).max(120),
      headline: z.string().max(200).optional(),
      bio: z.string().max(2000).optional(),
      public: z.boolean().default(false),
    }).parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase.from("lxp_portfolio_settings").upsert(
      { user_id: userId, ...data, headline: data.headline ?? null, bio: data.bio ?? null },
      { onConflict: "user_id" },
    );
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const getPublicPortfolio = createServerFn({ method: "GET" })
  .inputValidator((i: unknown) => z.object({ userId: z.string().min(4).max(120) }).parse(i))
  .handler(async ({ data }) => {
    const sb = publicClient();
    const { data: profile } = await sb
      .from("lxp_portfolio_settings")
      .select("display_name,headline,bio,public,slug")
      .eq("user_id", data.userId)
      .eq("public", true)
      .maybeSingle();
    if (!profile) return { profile: null, competencies: [], certificates: [], samples: [] };

    const [{ data: comps }, { data: certRows }, { data: subs }] = await Promise.all([
      sb.from("lxp_competencies").select("competency_key,competency_label,xp,level").eq("user_id", data.userId).order("xp", { ascending: false }),
      sb.rpc("lxp_public_portfolio_certificates", { _user_id: data.userId }),
      sb.from("lxp_task_submissions")
        .select("id,body_html,task_index,run:lxp_simulation_runs(simulation:lxp_simulations(title,tasks))")
        .eq("user_id", data.userId).eq("portfolio_visible", true),
    ]);

    const certificates = (certRows ?? []).map((c: any) => ({
      certificate_number: c.certificate_number,
      verification_token: c.verification_token,
      issued_at: c.issued_at,
      simulation_title: c.simulation_title ?? "",
    }));

    const samples = (subs ?? []).map((s: any) => ({
      id: s.id,
      body_html: s.body_html,
      task_title: s.run?.simulation?.tasks?.[s.task_index]?.title ?? `Task ${s.task_index + 1}`,
      simulation_title: s.run?.simulation?.title ?? "",
    }));
    return { profile, competencies: comps ?? [], certificates, samples };
  });
